import { Button, Tabs, Tooltip, type Tab, type TabsController } from "@rothoblaas/design-system";
import classNames from "classnames";
import type { FC, ReactNode } from "react";
import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import useTabs from "../../../../../hooks/useTabs";
import type { Translation } from "../../../../../hooks/useTranslations";
import type { OperationMode } from "../../../../../http/lock_calculator/types";
import { useLockCalculatorCatalog } from "../../hooks/useLockCalculatorCatalog";
import styles from "../../LockCalculator.module.css";
import type { CalculationCoefficients, LockCalculatorFormValues } from "../../model/form";
import { noticeStep, type CalculatorNotice, type NoticeSeverity } from "../../model/notices";
import { getLastReachableStep } from "../../model/stepsCompleteness";
import { ConnectorsStep } from "../connectors/ConnectorsStep";
import { EnvironmentStep } from "../environment/EnvironmentStep";
import { LoadsStep } from "../loads/LoadsStep";
import { useNotices } from "../notices/NoticesContext";
import { StructureStep } from "../structure/StructureStep";
import { RESULTS_TAB_OF_OPERATION, RESULTS_TAB_PARAMETER } from "../../model/resultsTabs";

const BADGE_CLASS: Record<NoticeSeverity, string> = {
  error: styles.wizardStepBadgeError,
  warning: styles.wizardStepBadgeWarning,
  info: styles.wizardStepBadgeInfo,
};

/** How many notices a step has, in the colour of the most severe one (the list is sorted). */
const StepBadge: FC<{ notices: CalculatorNotice[]; t: Translation }> = ({ notices, t }) => {
  const worst = notices[0]?.severity;
  if (!worst) return null;
  return (
    <span className={classNames(styles.wizardStepBadge, BADGE_CLASS[worst])} title={t.lockCalculatorModule.notices.count(notices.length)}>
      {notices.length}
    </span>
  );
};

const STEP_COUNT = 4;

const WizardStepWrapper: FC<{
  children: ReactNode;
  t: Translation;
  controller: TabsController;
  stepIndex: number;
  lastReachableStep: number;
}> = ({ children, t, controller, stepIndex, lastReachableStep }) => (
  <div className={styles.wizardStepWrapper}>
    {children}
    <div className={styles.wizardStepNav}>
      {stepIndex > 0 && (
        <Button
          type="button"
          variant="secondary"
          color="var(--accent-base, #00a9c0)"
          className={styles.wizardStepNavBack}
          onClick={() => controller.goToTab(stepIndex - 1)}
        >
          {t.common.back}
        </Button>
      )}
      <span className={styles.wizardNavStep}>{t.lockCalculatorModule.wizard.stepOf(stepIndex + 1, STEP_COUNT)}</span>
      {stepIndex < STEP_COUNT - 1 && (
        // Grid placement on our own span: with no content the Tooltip renders the bare Button, dropping its className.
        <span className={styles.wizardStepNavNext}>
          <Tooltip
            placement="left"
            className={styles.wizardStepNavTooltip}
            content={stepIndex < lastReachableStep ? null : t.lockCalculatorModule.wizard.completeFieldsToContinue}
          >
            <Button
              type="button"
              variant="secondary"
              color="var(--accent-base, #00a9c0)"
              disabled={stepIndex >= lastReachableStep}
              onClick={() => controller.goToTab(stepIndex + 1)}
            >
              {t.common.next}
            </Button>
          </Tooltip>
        </span>
      )}
    </div>
  </div>
);

type Props = {
  t: Translation;
  readOnly: boolean;
  isAdmin: boolean;
  currentStep: number;
  goToStep: (step: number, extraParams?: Record<string, string>) => void;
  onOpenHelp: (text: string, image?: string) => void;
  /** False until a configuration (draft or manual) has been chosen — Connectors has nothing to show/edit yet. */
  connectorsEnabled: boolean;
  /** Saves the current form snapshot and requests a generation/recomputation side-effect. */
  onTriggerOperation: (operationMode: OperationMode) => Promise<void>;
  /** True while a triggered operation's request is in flight. */
  isTriggeringOperation: boolean;
  /** Coefficients resolved by the backend on save, shown in the code/norm step. */
  coefficients: CalculationCoefficients;
};

export const CalculatorWizard = ({
  t,
  readOnly,
  isAdmin,
  currentStep,
  goToStep,
  onOpenHelp,
  connectorsEnabled,
  onTriggerOperation,
  isTriggeringOperation,
  coefficients,
}: Props) => {
  const steps = useMemo(
    () => [
      t.lockCalculatorModule.tabs.codeNorm,
      t.lockCalculatorModule.tabs.loads,
      t.lockCalculatorModule.tabs.structure,
      t.lockCalculatorModule.tabs.connectors,
    ],
    [t],
  );

  const tabsController = useTabs(steps.length, "lock_calc_tab");
  const values = useWatch({ control: useFormContext<LockCalculatorFormValues>().control }) as LockCalculatorFormValues;
  const { standards } = useLockCalculatorCatalog();
  const isEC5 = standards.find((s) => s.id === values.environment.standardId)?.code === "EC5";
  // Same gate for the stepper, back/next and the Structure step's buttons into Connectors.
  const lastReachableStep = getLastReachableStep(values, isEC5);

  // Notices per step: how many, and the worst severity (the list arrives most severe first).
  const { notices } = useNotices();
  const noticesByStep = steps.map((_, index) => notices.filter((notice) => noticeStep(notice) === index));

  const tabs = steps.map((title, index) => ({
    title,
    disabled: index === 3 && !connectorsEnabled,
  })) satisfies Tab[];
  // Rendered by each step inside its card, above the title.
  const stepper = (
    <nav className={styles.wizard} aria-label={t.lockCalculatorModule.aria.calculationSteps}>
      {steps.map((title, index) => (
        <button
          key={title}
          type="button"
          className={classNames(styles.wizardStep, {
            [styles.wizardStepActive]: index === currentStep,
            [styles.wizardStepDone]: index < currentStep,
          })}
          disabled={index > lastReachableStep}
          onClick={() => goToStep(index)}
        >
          <span className={styles.wizardIndex}>{index < currentStep ? "✓" : index + 1}</span>
          <span>{title}</span>
          <StepBadge notices={noticesByStep[index] ?? []} t={t} />
        </button>
      ))}
    </nav>
  );

  // The connectors step stays; the results on the right show what the button produced.
  const showResultsOf = (operationMode: OperationMode) =>
    goToStep(3, { [RESULTS_TAB_PARAMETER]: String(RESULTS_TAB_OF_OPERATION[operationMode]) });

  const contents = [
    <WizardStepWrapper t={t} controller={tabsController} stepIndex={0} lastReachableStep={lastReachableStep}>
      <EnvironmentStep readOnly={readOnly} onOpenHelp={onOpenHelp} coefficients={coefficients} stepper={stepper} />

    </WizardStepWrapper>,
    <WizardStepWrapper t={t} controller={tabsController} stepIndex={1} lastReachableStep={lastReachableStep}>
      <LoadsStep readOnly={readOnly} onOpenHelp={onOpenHelp} stepper={stepper} />
    </WizardStepWrapper>,
    <WizardStepWrapper t={t} controller={tabsController} stepIndex={2} lastReachableStep={lastReachableStep}>
      <StructureStep
        readOnly={readOnly}
        onOpenHelp={onOpenHelp}
        goToConnectorsStep={() => goToStep(3)}
        onGeneratedAll={() => showResultsOf("generateAll")}
        canContinue={lastReachableStep > 2}
        stepper={stepper}
        onTriggerOperation={onTriggerOperation}
        isTriggeringOperation={isTriggeringOperation}
      />
    </WizardStepWrapper>,
    <WizardStepWrapper t={t} controller={tabsController} stepIndex={3} lastReachableStep={lastReachableStep}>
      <ConnectorsStep
        readOnly={readOnly || !connectorsEnabled}
        isAdmin={isAdmin}
        stepper={stepper}
        onOpenHelp={onOpenHelp}
        onTriggerOperation={onTriggerOperation}
        onGenerated={showResultsOf}
        isTriggeringOperation={isTriggeringOperation}
      />
    </WizardStepWrapper>,
  ];

  return (
    <div className={styles.wizardViewport}>
      <div className={styles.hideTabsHeader}>
        <Tabs urlParameter="lock_calc_tab" tabs={tabs} contents={contents} renderMode="eager" />
      </div>
    </div>
  );
};
